import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { User } from '@models/user';
import { BillingTabManagerService } from '@services/billing-tab-manager.service';
import { LoaderService } from '@services/loader.service';
import { UserService } from '@services/user.service';
import { extendDeep, flattenObject } from '@utils/utils';
import { customExactLength } from '@utils/validators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'wsb-billing-details',
  templateUrl: './billing-details.component.html',
  styleUrls: ['./billing-details.component.scss'],
  standalone: false
})
export class BillingDetailsComponent implements OnInit, OnDestroy {
  @Input() user: User;

  billingForm: UntypedFormGroup = null;
  userIsPrivate: boolean;
  submitForm: Subscription;
  resetForm: Subscription;
  constructor(
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private loaderService: LoaderService,
    private billingTabManagerService: BillingTabManagerService
  ) {}

  ngOnDestroy(): void {
    this.submitForm.unsubscribe();
    this.resetForm.unsubscribe();
  }

  ngOnInit(): void {
    this.userIsPrivate = this.user.account.type === 'Private' ? true : false;
    this.initBillingTab();

    this.submitForm = this.billingTabManagerService.save$.subscribe(() => {
      this.submitBillingForm();
    });

    this.resetForm = this.billingTabManagerService.resetForm$.subscribe(() => {
      this.billingForm.reset(this.user);
    });

    this.billingForm.patchValue(this.user);
    this.loaderService.hide();
    this.billingForm.get('billing.province').patchValue(this.billingForm.get('billing.province').value.toUpperCase());
    if (!this.userIsPrivate) {
      this.billingForm
        .get('billing')
        .get('vatCode')
        .setValidators(this.user.nation === 'ita' ? [Validators.required, customExactLength()] : []);
      this.billingForm
        .get('billing')
        .get('companyName')
        .setValidators(Validators.required);
    }

    this.billingForm
      .get('billing')
      .get('province')
      .setValidators(this.user.nation === 'ita' ? [Validators.pattern('[a-zA-Z]*'), Validators.minLength(2), Validators.maxLength(2)] : []);
  }

  initBillingTab() {
    this.billingForm = this.formBuilder.group({
      billing: this.formBuilder.group({
        address: ['', [Validators.required]],
        city: ['', [Validators.required]],
        companyName: [null, [Validators.maxLength(40)]],
        province: [''],
        taxCode: [''],
        vatCode: [null],
        paCode: [''],
        zipCode: ['', [Validators.required]],
        invoicingEmail: ['', [Validators.email]]
      })
    });

    this.billingForm.valueChanges.subscribe(() => {
      this.billingTabManagerService.allowChangeTab(this.billingForm);
      this.billingTabManagerService.disableSave(this.billingForm);
    });

    this.billingForm.get('billing.province').valueChanges.subscribe(() => {
      this.billingForm
        .get('billing.province')
        .patchValue(this.billingForm.get('billing.province').value.toUpperCase(), { emitEvent: false });
    });
  }

  submitBillingForm() {
    const formObject = flattenObject(this.user) as any;
    const billingData = {
      billing: this.extractNotNullPropertyAndValue(this.billingForm.get('billing').value)
    };
    extendDeep(formObject, billingData); // User object from api may change, and we never delete any property
    delete formObject.password; // not really used
    // new

    this.userService.updateUserBilling(this.user, formObject.billing).subscribe(
      savedBilling => {
        this.user.billing = savedBilling;
        this.billingForm.reset(this.user);
        this.billingTabManagerService.successMessage('common_success');
        this.billingForm.enable();
      },
      error => {
        this.billingTabManagerService.errorMessage('common_error');
        this.billingForm.enable();
      }
    );
  }

  extractNotNullPropertyAndValue(obj: any) {
    const result = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== null) {
        result[key] = obj[key];
      }
    }
    return result;
  }
}
